import { createBrowserRouter, Navigate, Outlet, useRouteError, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/providers/auth-provider";
import { WsProvider } from "@/providers/ws-provider";
import LoginPage from "@/pages/login-page";
import HomePage from "@/pages/home-page";
import GridsPage from "@/pages/grids-page";
import GridDetailPage from "@/pages/grid-detail-page";
import WindPage from "@/pages/wind-page";
import WindDetailPage from "@/pages/wind-detail-page";
import BacktestListPage from "@/pages/backtest-list-page";
import ActivityPage from "@/pages/activity-page";
import AppLayout from "@/components/shell/app-layout";
import DetailLayout from "@/components/shell/detail-layout";

/** Root layout — renders providers inside the router tree so parent
 *  re-renders (auth/ws state) don't conflict with RouterProvider's
 *  internal DOM management. */
function RootLayout() {
  return (
    <AuthProvider>
      <WsProvider>
        <Outlet />
      </WsProvider>
    </AuthProvider>
  );
}

function RequireAuth() {
  const { isAuthed, checking } = useAuth();
  if (checking) return null;
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function ErrorFallback() {
  const error = useRouteError() as Error | { status?: number; statusText?: string; data?: unknown };
  const message = error instanceof Error
    ? error.message
    : `${(error as any).status ?? ""} ${(error as any).statusText ?? "Unknown error"}`.trim();
  const stack = error instanceof Error ? error.stack : undefined;
  const statusOrData = error instanceof Error
    ? null
    : { status: (error as any).status, data: (error as any).data };

  // Surface full context to the browser console so we can debug next time
  // the user hits an intermittent render/route error (e.g. the recurring
  // "The object can not be found here" on wind detail during position
  // transitions). Includes the URL and navigator state to correlate.
  if (typeof console !== "undefined") {
    console.error("[RouteError]", {
      message, stack, error, statusOrData,
      href: typeof window !== "undefined" ? window.location.href : null,
      referrer: typeof document !== "undefined" ? document.referrer : null,
      ts: new Date().toISOString(),
    });
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3 px-4 text-center">
      <p className="text-danger text-sm">{message}</p>
      <Link to="/" className="text-accent text-xs hover:underline">Back to Home</Link>
      {/* Dev-only stack for quick debugging. Hidden in prod so users aren't
          spooked by minified traces. */}
      {import.meta.env.DEV && stack && (
        <pre className="text-[0.6rem] text-dim whitespace-pre-wrap max-w-md overflow-auto max-h-48 text-left">
          {stack}
        </pre>
      )}
    </div>
  );
}

export const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      children: [
        { path: "/login", element: <LoginPage /> },
        {
          element: <RequireAuth />,
          errorElement: <ErrorFallback />,
          children: [
            // List routes: shell with tab bar
            {
              element: <AppLayout />,
              children: [
                { path: "/", element: <HomePage /> },
                { path: "/grids", element: <GridsPage /> },
                { path: "/wind", element: <WindPage /> },
                { path: "/activity", element: <ActivityPage /> },
                { path: "/backtest", element: <BacktestListPage /> },
              ],
            },
            // Detail routes: minimal shell (top bar + back btn, no tabs/stats)
            {
              element: <DetailLayout />,
              children: [
                { path: "/grid/:id", element: <GridDetailPage /> },
                { path: "/wind/:id", element: <WindDetailPage /> },
              ],
            },
          ],
        },
        // Catch-all: redirect unknown paths to home
        { path: "*", element: <Navigate to="/" replace /> },
      ],
    },
  ],
  { basename: "/" }
);
